import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge } from 'react-bootstrap';
import { FaUsers, FaCreditCard, FaTicketAlt } from 'react-icons/fa';
import { adminDashboardService } from '../../services/adminDashboardService';
import { showToast } from '../../utils/toast';
import Spinner from '../common/Spinner';

const AdminHomeTab = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalUsers: 0,
      activeUsers: 0,
      totalBlogs: 0,
      openTickets: 0
    },
    recentUsers: [],
    recentTickets: []
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await adminDashboardService.getDashboardStats();
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showToast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { 
      title: 'Total Users', 
      value: dashboardData.stats.totalUsers.toLocaleString(), 
      icon: <FaUsers />, 
      color: 'primary' 
    },
    { 
      title: 'Active Users', 
      value: dashboardData.stats.activeUsers.toLocaleString(), 
      icon: <FaCreditCard />, 
      color: 'success' 
    },
    // { 
    //   title: 'Total Blogs', 
    //   value: dashboardData.stats.totalBlogs.toLocaleString(), 
    //   icon: <FaBlog />, 
    //   color: 'info' 
    // },
    { 
      title: 'Open Tickets', 
      value: dashboardData.stats.openTickets.toLocaleString(), 
      icon: <FaTicketAlt />, 
      color: 'warning' 
    },
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getUserName = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.email;
  };

  const getTicketSubject = (ticket) => {
    return ticket.request_message.length > 50 
      ? ticket.request_message.substring(0, 50) + '...'
      : ticket.request_message;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'pending': 'warning',
      'resolved': 'success',
      'closed': 'secondary'
    };
    return statusColors[status] || 'secondary';
  };

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-3">Admin Dashboard</h2>
          <p className="text-muted">Welcome to the admin panel. Here's an overview of your system.</p>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row className="mb-4">
        {loading ? (
          <Col className="text-center py-4">
            <Spinner />
            <p className="mt-2">Loading dashboard statistics...</p>
          </Col>
        ) : (
          stats.map((stat, index) => (
            <Col key={index} lg={3} md={6} className="mb-3">
              <Card className={`border-${stat.color} h-100`}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="card-title text-muted">{stat.title}</h6>
                      <h3 className="mb-0">{stat.value}</h3>
                    </div>
                    <div className={`text-${stat.color} fs-1`}>
                      {stat.icon}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))
        )}
      </Row>

      <Row>
        {/* Recent Users */}
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Recent Users</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="text-center py-3">
                        <Spinner size="sm" />
                      </td>
                    </tr>
                  ) : dashboardData?.recentUsers?.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-3 text-muted">
                        No recent users found
                      </td>
                    </tr>
                  ) : (
                    dashboardData.recentUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{getUserName(user)}</td>
                        <td>{user.email}</td>
                        <td>
                          <Badge bg={user.is_account_active ? 'success' : 'secondary'}>
                            {user.is_account_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>{formatDate(user.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* Recent Support Tickets */}
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Recent Support Tickets</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>User</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="text-center py-3">
                        <Spinner size="sm" />
                      </td>
                    </tr>
                  ) : dashboardData?.recentTickets?.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-3 text-muted">
                        No recent tickets found
                      </td>
                    </tr>
                  ) : (
                    dashboardData.recentTickets.map((ticket) => (
                      <tr key={ticket.id}>
                        <td>
                          <div title={ticket.request_message}>
                            {getTicketSubject(ticket)}
                          </div>
                        </td>
                        <td>{ticket.user_email}</td>
                        <td>
                          <Badge bg={getStatusBadge(ticket.status)}>
                            {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                          </Badge>
                        </td>
                        <td>{formatDate(ticket.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminHomeTab; 